/**
 * NewsAdminPage.jsx — src/content/Manager/News/NewsAdminPage.jsx
 * Роут: /news   (только head_manager / administrator)
 */
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Button, Chip, CircularProgress, IconButton, Input, Modal, ModalDialog,
    Option, Select, Sheet, Stack, Tooltip, Typography,
} from '@mui/joy';
import { AnimatePresence, motion } from 'framer-motion';

import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import UnpublishedRoundedIcon from '@mui/icons-material/UnpublishedRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';

import { LayoutContext } from '../../../context';
import {
    APICreateNewsAdmin, APIDeleteNewsAdmin, APIDuplicateNewsAdmin,
    APIGetNewsAdminList, APIGetNewsAdminPreview, APISetNewsAdminStatus,
} from '../../../api';
import NewsStatsTab from './NewsStatsTab';

const STATUS_META = {
    draft:     { label: 'Черновик',   color: '#94a3b8', bg: 'rgba(148,163,184,0.14)' },
    published: { label: 'Опубликована', color: '#16a34a', bg: 'rgba(22,163,74,0.12)' },
    archived:  { label: 'В архиве',   color: '#a16207', bg: 'rgba(161,98,7,0.12)' },
};

const AUDIENCE_LABELS = {
    employees: 'Сотрудники',
    clients: 'Клиенты',
    all: 'Все',
};

const SHOW_MODE_LABELS = {
    exact_day: 'День в день',
    range: 'Диапазон',
    always: 'Всегда',
};

const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return iso; }
};

const fade = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 },
    transition: { duration: 0.18 },
};


/* ─────────────────────────── Карточка новости ─────────────────────────── */
function NewsCard({ item, onOpen, onPreview, onStats, onToggleStatus, onDuplicate, onDelete, busy }) {
    const meta = STATUS_META[item.status] || STATUS_META.draft;
    const accent = item.accent_color || '#6366f1';
    const preview = item.first_slide_preview;
    const image = item.cover_url || preview?.image_url || null;

    return (
        <Sheet
            component={motion.div}
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            sx={{
                borderRadius: '18px',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all .2s',
                '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 14px 36px rgba(15,23,42,0.12)',
                    borderColor: accent,
                },
            }}
        >
            {/* Обложка */}
            <Box
                onClick={() => onOpen(item.id)}
                sx={{
                    position: 'relative', height: 140, cursor: 'pointer',
                    background: image
                        ? `url(${image}) center/cover no-repeat`
                        : `linear-gradient(135deg, ${accent} 0%, #1e1b3a 100%)`,
                }}
            >
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.45), transparent 60%)' }} />

                <Chip size="sm" sx={{
                    position: 'absolute', top: 10, left: 10,
                    background: meta.bg, color: meta.color,
                    fontWeight: 800, fontSize: '0.68rem', border: 'none',
                    backdropFilter: 'blur(6px)',
                }}>
                    {meta.label}
                </Chip>

                <Chip size="sm" startDecorator={<LayersRoundedIcon sx={{ fontSize: 13 }} />} sx={{
                    position: 'absolute', bottom: 10, right: 10,
                    background: 'rgba(0,0,0,0.42)', color: '#fff',
                    fontWeight: 700, fontSize: '0.68rem', border: 'none',
                    backdropFilter: 'blur(6px)',
                }}>
                    {item.slides_count || 0}
                </Chip>
            </Box>

            {/* Тело */}
            <Box sx={{ p: 1.8, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                    onClick={() => onOpen(item.id)}
                    sx={{
                        fontWeight: 800, fontSize: '1rem', lineHeight: 1.3, mb: 0.4,
                        cursor: 'pointer', '&:hover': { color: accent },
                    }}
                >
                    {item.title || 'Без названия'}
                </Typography>

                {item.subtitle && (
                    <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1, lineHeight: 1.4 }}>
                        {item.subtitle}
                    </Typography>
                )}

                <Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap', gap: 0.6, mb: 1.2 }}>
                    <Chip size="sm" variant="soft" color="neutral" startDecorator={<EventRoundedIcon sx={{ fontSize: 13 }} />} sx={{ fontSize: '0.68rem' }}>
                        {item.show_mode === 'always' ? SHOW_MODE_LABELS.always : fmtDate(item.show_date)}
                    </Chip>
                    <Chip size="sm" variant="soft" color="neutral" sx={{ fontSize: '0.68rem' }}>
                        {AUDIENCE_LABELS[item.audience_type] || item.audience_type}
                    </Chip>
                    {(item.target_positions || []).length > 0 && (
                        <Chip size="sm" variant="soft" color="primary" sx={{ fontSize: '0.68rem' }}>
                            {item.target_positions.length} должн.
                        </Chip>
                    )}
                    {item.is_mandatory && (
                        <Chip size="sm" variant="soft" color="danger" sx={{ fontSize: '0.68rem' }}>Обязательная</Chip>
                    )}
                </Stack>

                <Stack direction="row" spacing={2} sx={{ mt: 'auto', mb: 1.2 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <GroupRoundedIcon sx={{ fontSize: 15, color: 'text.tertiary' }} />
                        <Typography level="body-xs" sx={{ fontWeight: 700 }}>{item.views_total || 0}</Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>увидели</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <DoneAllRoundedIcon sx={{ fontSize: 15, color: 'text.tertiary' }} />
                        <Typography level="body-xs" sx={{ fontWeight: 700 }}>{item.views_completed || 0}</Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>досмотрели</Typography>
                    </Stack>
                </Stack>

                <Stack direction="row" spacing={0.5} justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={0.3}>
                        <Tooltip title="Статистика" size="sm">
                            <IconButton size="sm" variant="soft" color="neutral" onClick={() => onStats(item.id)}>
                                <InsightsRoundedIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Предпросмотр" size="sm">
                            <IconButton size="sm" variant="soft" color="neutral" onClick={() => onPreview(item.id)}>
                                <VisibilityRoundedIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Дублировать" size="sm">
                            <IconButton size="sm" variant="soft" color="neutral" disabled={busy} onClick={() => onDuplicate(item.id)}>
                                <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить" size="sm">
                            <IconButton size="sm" variant="soft" color="danger" onClick={() => onDelete(item)}>
                                <DeleteForeverRoundedIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    {item.status === 'published' ? (
                        <Button
                            size="sm" variant="soft" color="warning" disabled={busy}
                            startDecorator={<UnpublishedRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={() => onToggleStatus(item, 'draft')}
                            sx={{ fontSize: '0.75rem' }}
                        >
                            Снять
                        </Button>
                    ) : (
                        <Button
                            size="sm" color="success" disabled={busy}
                            startDecorator={<PublishRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={() => onToggleStatus(item, 'published')}
                            sx={{ fontSize: '0.75rem' }}
                        >
                            Опубликовать
                        </Button>
                    )}
                </Stack>
            </Box>
        </Sheet>
    );
}


/* ─────────────────────────── Вкладка «Новости» ─────────────────────────── */
function NewsListTab() {
    const navigate = useNavigate();
    const { tokenEmployee, notify, setNewsStories } = useContext(LayoutContext);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const [status, setStatus] = useState('');
    const [audience, setAudience] = useState('');
    const [q, setQ] = useState('');
    const [qDebounced, setQDebounced] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setQDebounced(q), 400);
        return () => clearTimeout(t);
    }, [q]);

    const load = useCallback(async () => {
        setLoading(true);
        const data = await APIGetNewsAdminList(tokenEmployee, {
            status, audience_type: audience, q: qDebounced, page, page_size: 12,
        });
        setItems(data?.results || []);
        setPages(data?.num_pages || 1);
        setLoading(false);
    }, [tokenEmployee, status, audience, qDebounced, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [status, audience, qDebounced]);

    const handleCreate = async () => {
        setBusy(true);
        const today = new Date().toISOString().slice(0, 10);
        const created = await APICreateNewsAdmin({
            title: 'Новая новость',
            status: 'draft',
            show_mode: 'exact_day',
            show_date: today,
            audience_type: 'employees',
        }, tokenEmployee);
        setBusy(false);

        if (created?._error || !created?.id) {
            notify('Не удалось создать новость', 'danger');
            return;
        }
        navigate(`/news/${created.id}`);
    };

    const handlePreview = async (id) => {
        const data = await APIGetNewsAdminPreview(id, tokenEmployee);
        if (data?._error || !(data?.slides || []).length) {
            notify('В новости пока нет активных слайдов', 'warning');
            return;
        }
        setNewsStories({ open: true, items: [data], preview: true });
    };

    const handleToggleStatus = async (item, next) => {
        setBusy(true);
        const res = await APISetNewsAdminStatus(item.id, next, tokenEmployee);
        setBusy(false);
        if (res?._error) {
            notify(res?.error || 'Не удалось изменить статус', 'danger');
            return;
        }
        notify(next === 'published' ? 'Новость опубликована' : 'Новость снята с публикации', 'success');
        load();
    };

    const handleDuplicate = async (id) => {
        setBusy(true);
        const res = await APIDuplicateNewsAdmin(id, tokenEmployee);
        setBusy(false);
        if (res?._error) { notify('Не удалось дублировать', 'danger'); return; }
        notify('Создана копия — черновик', 'success');
        load();
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        const id = confirmDelete.id;
        setConfirmDelete(null);
        setBusy(true);
        const res = await APIDeleteNewsAdmin(id, tokenEmployee);
        setBusy(false);
        if (res?._error) { notify('Не удалось удалить', 'danger'); return; }
        notify('Новость удалена', 'success');
        load();
    };

    return (
        <Box>
            {/* Фильтры */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2.5 }}>
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Поиск по названию"
                    startDecorator={<SearchRoundedIcon sx={{ fontSize: 18 }} />}
                    sx={{ flex: 1, minWidth: 200 }}
                />
                <Select value={status} onChange={(e, v) => setStatus(v ?? '')} placeholder="Статус" sx={{ minWidth: 160 }}>
                    <Option value="">Все статусы</Option>
                    <Option value="draft">Черновики</Option>
                    <Option value="published">Опубликованные</Option>
                    <Option value="archived">Архив</Option>
                </Select>
                <Select value={audience} onChange={(e, v) => setAudience(v ?? '')} placeholder="Аудитория" sx={{ minWidth: 160 }}>
                    <Option value="">Любая аудитория</Option>
                    <Option value="employees">Сотрудники</Option>
                    <Option value="clients">Клиенты</Option>
                    <Option value="all">Все</Option>
                </Select>
                <Button
                    startDecorator={<AddRoundedIcon />}
                    onClick={handleCreate}
                    loading={busy}
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                    Создать новость
                </Button>
            </Stack>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : items.length === 0 ? (
                <Sheet sx={{ borderRadius: '18px', border: '1px dashed', borderColor: 'divider', p: 6, textAlign: 'center' }}>
                    <CampaignRoundedIcon sx={{ fontSize: 46, color: 'text.tertiary', mb: 1 }} />
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', mb: 0.5 }}>Пока ни одной новости</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                        Расскажите команде о новых возможностях сервиса — это займёт пару минут
                    </Typography>
                    <Button startDecorator={<AddRoundedIcon />} onClick={handleCreate} loading={busy}>
                        Создать первую новость
                    </Button>
                </Sheet>
            ) : (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                    gap: 2,
                }}>
                    <AnimatePresence>
                        {items.map(item => (
                            <NewsCard
                                key={item.id}
                                item={item}
                                busy={busy}
                                onOpen={(id) => navigate(`/news/${id}`)}
                                onStats={(id) => navigate(`/news/${id}/stats`)}
                                onPreview={handlePreview}
                                onToggleStatus={handleToggleStatus}
                                onDuplicate={handleDuplicate}
                                onDelete={setConfirmDelete}
                            />
                        ))}
                    </AnimatePresence>
                </Box>
            )}

            {pages > 1 && (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 3 }}>
                    <Button variant="soft" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Назад</Button>
                    <Button variant="plain" disabled>{page} / {pages}</Button>
                    <Button variant="soft" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Вперёд</Button>
                </Stack>
            )}

            <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
                <ModalDialog sx={{ borderRadius: '18px', maxWidth: 420 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 1 }}>Удалить новость?</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                        «{confirmDelete?.title}» будет удалена вместе со слайдами и статистикой просмотров.
                        Действие необратимо — если нужно просто скрыть, переведите в архив.
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="soft" color="neutral" onClick={() => setConfirmDelete(null)}>Отмена</Button>
                        <Button color="danger" onClick={handleDelete}>Удалить</Button>
                    </Stack>
                </ModalDialog>
            </Modal>
        </Box>
    );
}



/* ─────────────────────────── Страница ─────────────────────────── */
export default function NewsAdminPage() {
    const [tab, setTab] = useState('news');

    return (
        <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', minWidth: 0 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{
                    width: 42, height: 42, borderRadius: '13px',
                    background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                }}>
                    <CampaignRoundedIcon sx={{ color: 'white', fontSize: 21 }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.5px' }}>Новости</Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        Сторис о нововведениях сервиса: показ по дате, аудитории и статистика просмотров
                    </Typography>
                </Box>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: 'wrap' }}>
                <Button variant={tab === 'news' ? 'solid' : 'soft'} color="primary" startDecorator={<CampaignRoundedIcon />} onClick={() => setTab('news')} sx={{ borderRadius: 'lg' }}>Новости</Button>
                <Button variant={tab === 'stats' ? 'solid' : 'soft'} color="primary" startDecorator={<InsightsRoundedIcon />} onClick={() => setTab('stats')} sx={{ borderRadius: 'lg' }}>Статистика</Button>
            </Stack>

            <AnimatePresence mode="wait">
                {tab === 'news' && <motion.div key="news" {...fade}><NewsListTab /></motion.div>}
                {tab === 'stats' && <motion.div key="stats" {...fade}><NewsStatsTab /></motion.div>}
            </AnimatePresence>
        </Box>
    );
}